// ============================================================================
// MODULAR DESIGN PRINCIPLES (MANDATORY)
// ============================================================================
// This file strictly follows:
// 1. Single Responsibility Principle - Shows voice diagnostic logs only
// 2. Separation of Concerns - Fetch/copy UI separated from VoiceDetailModal
// 3. Modular Architecture - Reusable diagnostic section component
//
// Refer to: docs/architecture/modular-design-standards.md
// Refer to: docs/manual/02-voice-process.md
// Refer to: docs/implementation/voice-diagnostic-logs.md
// ============================================================================

import React, { useMemo, useState } from 'react';
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Alert,
  Box,
  Button,
  CircularProgress,
  Stack,
  Typography,
} from '@mui/material';
import { ContentCopy, ExpandMore, Refresh } from '@mui/icons-material';
import { apiClient, API_ENDPOINTS } from '../config/api';

function metricLine(metric) {
  return [
    metric.metric_name || metric.metric_key || 'Metric',
    `result=${metric.result ?? 'N/A'}`,
    `status=${metric.status ?? 'N/A'}`,
    `passed=${metric.passed ?? 'N/A'}`,
    `score=${metric.score ?? 'N/A'}`,
  ].join(' | ');
}

function compactSummary(logs) {
  if (!logs) return 'No diagnostic logs loaded.';
  const task = logs.task || {};
  const analysis = logs.analysis || {};
  const warnings = logs.warnings || [];
  return [
    `Task: ${task.file_name || task.id || 'N/A'} (${task.status || 'unknown'})`,
    `QA Score: ${analysis.qa_score ?? 'N/A'} | Mode: ${analysis.qa_scoring_mode || 'N/A'}`,
    `Warnings: ${warnings.length}`,
    `Metrics: ${(logs.qa_metrics || []).length}`,
  ].join('\n');
}

const VoiceDiagnosticLogSection = ({ taskId, readOnly = false }) => {
  const [expanded, setExpanded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [logs, setLogs] = useState(null);
  const [copyMessage, setCopyMessage] = useState('');

  const displayText = useMemo(() => {
    if (!logs) return '';
    return logs.copy_text || JSON.stringify(logs, null, 2);
  }, [logs]);

  const fetchLogs = async () => {
    if (!taskId || readOnly) return;
    setLoading(true);
    setError('');
    try {
      const response = await apiClient.get(
        `${API_ENDPOINTS.VOICE_DIAGNOSTIC_LOGS}/${taskId}/diagnostic-logs`
      );
      setLogs(response.data?.diagnostic_logs || null);
    } catch (err) {
      const detail = err.response?.data?.detail || err.message || 'Failed to load diagnostic logs.';
      setError(String(detail));
    } finally {
      setLoading(false);
    }
  };

  const handleToggle = (_, nextExpanded) => {
    setExpanded(nextExpanded);
    if (nextExpanded && !logs && !loading) {
      fetchLogs();
    }
  };

  const copyValue = async (value, label) => {
    try {
      await navigator.clipboard.writeText(value || '');
      setCopyMessage(`${label} copied.`);
    } catch {
      setCopyMessage('Copy failed. Check browser clipboard permission.');
    }
  };

  if (!taskId || readOnly) {
    return null;
  }

  return (
    <Accordion expanded={expanded} onChange={handleToggle} sx={{ mt: 3 }}>
      <AccordionSummary expandIcon={<ExpandMore />}>
        <Box>
          <Typography variant="h6">Diagnostic Logs</Typography>
          <Typography variant="body2" color="text.secondary">
            Expand to load a redacted debug bundle for voice process and QA analysis.
          </Typography>
        </Box>
      </AccordionSummary>
      <AccordionDetails>
        <Stack spacing={2}>
          {error && <Alert severity="error">{error}</Alert>}
          {copyMessage && <Alert severity="success">{copyMessage}</Alert>}
          <Stack direction="row" spacing={1} flexWrap="wrap">
            <Button
              size="small"
              variant="outlined"
              startIcon={<Refresh />}
              onClick={fetchLogs}
              disabled={loading}
            >
              Refresh Logs
            </Button>
            <Button
              size="small"
              variant="contained"
              startIcon={<ContentCopy />}
              onClick={() => copyValue(displayText, 'Diagnostic text')}
              disabled={!logs}
            >
              Copy Logs
            </Button>
            <Button
              size="small"
              variant="outlined"
              startIcon={<ContentCopy />}
              onClick={() => copyValue(logs?.json_text || JSON.stringify(logs, null, 2), 'Diagnostic JSON')}
              disabled={!logs}
            >
              Copy JSON
            </Button>
          </Stack>
          {loading ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CircularProgress size={20} />
              <Typography variant="body2">Loading diagnostic logs...</Typography>
            </Box>
          ) : (
            <>
              <Typography
                component="pre"
                variant="body2"
                sx={{
                  p: 2,
                  bgcolor: 'grey.50',
                  border: '1px solid',
                  borderColor: 'grey.300',
                  borderRadius: 1,
                  whiteSpace: 'pre-wrap',
                  maxHeight: 360,
                  overflow: 'auto',
                  fontFamily: 'monospace',
                }}
              >
                {logs ? displayText : compactSummary(logs)}
              </Typography>
              {logs?.qa_metrics?.length > 0 && (
                <Box>
                  <Typography variant="subtitle2" gutterBottom>
                    Metric Summary
                  </Typography>
                  <Stack spacing={0.5}>
                    {logs.qa_metrics.map((metric) => (
                      <Typography key={metric.metric_key || metric.metric_name} variant="caption">
                        {metricLine(metric)}
                      </Typography>
                    ))}
                  </Stack>
                </Box>
              )}
            </>
          )}
        </Stack>
      </AccordionDetails>
    </Accordion>
  );
};

export default VoiceDiagnosticLogSection;
